import React, { CSSProperties, Dispatch, SetStateAction } from 'react';
import { IOptionType } from '../Components/CustomUIElements/MultiSelect';
import { StyledSnackbarProps } from '../Components/CustomUIElements/StyledSnackbar';
import { IJobAttachment } from '../Components/Modals/FilePreviewModal';
import { sortableColumnsDefaults } from './Applications/config';
import {
  IAttendee,
  IEventType,
  ScoutCalendarAction,
  ScoutCalendarState
} from '../../ScoutCalendar/types';
import { ISortableColumns } from '../Jobs/types';
import { SxProps } from '@mui/material';

export interface IJob {
  id: number;
  reference: string;
  title: string;
  state: string;
  location: string;
  archived: boolean;
  expires_at: string;
  updated_at: string;
  public_url: string;
  description: string;
  created_at: string;
  created_by: { firstname: string; lastname: string; link: string };
  published_at: string;
  salary_info: string;
  entity: string;
  entity_id: number;
  entity_link: string;
  address_state: string;
  employment_status: string;
  brand: { id: number; name: string } | null;
  brand_link: string;
  summary: string;
  internal: boolean;
  job_statuses: { label: string; link: string }[];
  job_states: { label: string; link: string }[];
  external: boolean;
  postcode: string;
  user: string;
  applications_count: number;
  open_applications_count: number;
  job_link: string;
  assigned_user: {
    id: number;
    email: string;
    firstname: string;
    lastname: string;
  };
  tags: { id: number; name: string }[];
  tag_list: string;
  questions: {
    id: number;
    enabled: boolean;
    rateable: boolean;
    weight: number;
    required: boolean;
    position: number;
    title: string;
    type: string;
    field_choices: { id: number; name: string; position: number }[];
    confidential: boolean;
  }[];
  candidate_attachments: {
    label: string;
    value: string;
    required: boolean;
  }[];
  sources: { label: string; id: number }[];
  options: {
    blind_rating: number | string;
    hide_rating_activity: number | string;
    auto_ratings: number | string;
    title_field: number | string;
    gender_field: number | string;
    linkedin_fiel: number | string;
    indeed_apply: number | string;
  };
  privacy_acknowledgement: boolean;
  privacy_url: string;
  template: string;
  template_enabled: boolean;
  application_question_template: {
    id: number;
    job_id: number;
    created_at: Date;
    updated_at: Date;
  };
  integrations: {
    test_grid_configured: boolean;
    revolve_configured: boolean;
  };
  time_zone: string;
  entity_settings: {
    show_panel_workflow: boolean;
    show_smart_form_recipients: boolean;
  };
}

export type IStyle = {
  [className: string]: CSSProperties | IStyle;
};

export interface IJobsForTask {
  id: number;
  title: string;
}

export interface IApplicationsForTask {
  id: number;
  name: string;
}

export interface ITabs {
  children?: React.ReactNode;
  index: number;
  tabValue: number;
}

export interface ISubcontainerProps {
  applications: number;
  openApplications: number;
  jobCounts: IJobCounts;
  createdAt: string;
  publishedAt: string;
  expiresAt: string;
  handleTabChange: (event: React.ChangeEvent, newValue: number) => void;
  isLoading: boolean;
  timeZone: string;
}

export interface IAppsAndViewsProps {
  applications: number;
  openApplications: number;
  jobCounts: IJobCounts;
  isLoading: boolean;
  handleTabChange: (event: React.ChangeEvent, newValue: number) => void;
}

export interface ICreationExpiryProps {
  createdAt: string;
  publishedAt: string;
  expiresAt: string;
  isLoading: boolean;
  timeZone: string;
}

export interface IDateWidget {
  isLoading: boolean;
  label: string;
  timezone: string;
  utcDate: string;
  sx?: SxProps;
}

export interface IJobInformationProps {
  createdBy: string;
  createdByLink: string;
  entity: string;
  entityLink: string;
  brand: string | null;
  brandLink: string;
  location: string;
  tags: IJob['tags'];
  assignedUser: string;
  isLoading: boolean;
}

export interface IJobCounts {
  unique_page_views: {
    unique_views: number;
    daily_unique_views: number;
  };
  applications_count: number;
}

export interface ISingleMultiposterProps {
  apiKey: string;
  jobId: string;
  jobTitle: string;
  newApplyWithSeekEnabled: boolean;
}

export interface IJobEmailStatus {
  id: number;
  email_template: IEmailTemplate;
  created_by: {
    firstname: string;
    lastname: string;
    link: string;
  };
  created_at: string;
  from: string;
  applications_count: number;
  state: string;
  canceled_at: string;
  canceled_by: {
    firstname: string;
    lastname: string;
  };
  filters: {
    status: string;
    country: string;
    suburb: string;
    rating: string;
    withoutRating: Array<string>;
  };
  statistics: {
    bounced: number;
    bounced_pcent: number;
    clicks: number;
    clicks_pcent: number;
    delivered: number;
    delivered_pcent: number;
    opened: number;
    opened_pcent: number;
    unsubscribed: number;
    unsubscribed_pcent: number;
  };
  applications: { candidates: ICandidate[] };
}

export interface ICandidate {
  id: number;
  candidate: string;
  email: string;
  status: string;
}
export interface IJobNote {
  id: number;
  body: string;
  updated_at: string;
  noted_by: string;
}

export interface IApplicantAttachment {
  attached_content_type: string;
  attached_file_name: string;
  created_at: string;
  id: number;
  url: string;
}

export interface IJobApplication {
  id: number;
  created_at: string;
  rating: string;
  status: string;
  application_link: string;
  status_details: {
    id: number;
    name: string;
    position: number;
    colour: string;
  };
  candidate: {
    id: number;
    firstname: string;
    lastname: string;
    email: string;
    gender_full: string;
    phone1: string;
    phone2: string;
    existing_candidate: boolean;
    video_interview_url: string;
    address: string;
    tag_list: string[];
    address_details: {
      id: number;
      street1: string;
      street2: string;
      suburb: string;
      postcode: string;
      street_number: string;
      street_type: string;
      state: {
        id: number;
        name: string;
        abbreviation: string;
      };
      country: {
        id: number;
        name: string;
        iso: string;
        iso3: string;
        phone_prefix: string;
      };
    };
  };
  attachments: IApplicantAttachment[];
  notes: {
    id: number;
    subject: string;
    body: string;
    noted_by: string;
  }[];
  tag_list: string[];
  suitability_comment: string;
  sister_entity_application: boolean;
}

export interface IApplicationProps {
  jobAttachments: IJob['candidate_attachments'] | undefined;
  jobHasPrivacyAcknowledgement: boolean | undefined;
  jobId: number;
  jobIsLoading: boolean;
  jobOptions: IJob['options'];
  jobQuestions: IJob['questions'] | undefined;
  jobSources: IJob['sources'] | undefined;
  jobTitle: string;
  setJob: Dispatch<SetStateAction<IJob | null>>;
  newApplicationEnabled: boolean;
  csvExportEnabled: boolean;
  genderFieldEnabled: boolean;
}

export interface IApplicationStatus {
  colour: string;
  created_at: string;
  do_hire: boolean;
  id: number;
  name: string;
  position: number;
  state: string;
  visible: boolean;
}

export interface IApplicationCountry {
  id: number;
  name: string;
}

export interface IApplicationTableProps {
  applications: IJobApplication[];
  applicationStatuses: IApplicationStatus[];
  clearAllSelection: () => void;
  currentPage: number;
  density: string;
  enableSortableColumn: () => void;
  getAllApplications: IGetAllApplications;
  getApplications: IGetApplications;
  getFilterableStatuses: () => void;
  globalSelected: IJobApplication[];
  isLoading: boolean;
  jobId: number;
  jobTitle: string;
  rowsPerPage: number;
  selectAllApplications: boolean;
  selectAllIsChecked: boolean;
  selectAllIsIndeterminate: boolean;
  selected: number[];
  setApplications: Dispatch<SetStateAction<IJobApplication[]>>;
  setCurrentPage: (currentPage: number) => void;
  setGlobalSelected: React.Dispatch<React.SetStateAction<IJobApplication[]>>;
  setPreviewOpen: (previewOpen: boolean) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  setSelectAllApplications: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectAllIsChecked: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectAllIsIndeterminate: React.Dispatch<React.SetStateAction<boolean>>;
  setSelected: (selected: number[]) => void;
  setSelectedAttachment: (selectedAttachment: IJobAttachment) => void;
  setSnackbarState: StyledSnackbarProps['setSnackbarState'];
  setSortableColumns: IDropdownProps['setSortableColumns'];
  setSortBy: (sortBy: string) => void;
  setSortOrder: (sortOrder: string) => void;
  showGender: boolean;
  sortableColumns: IDropdownProps['sortableColumns'];
  sortBy: string;
  sortOrder: string;
  totalApplications: number;
  totalPages: number;
  newApplicationPage: boolean;
  refetchApplications: () => void;
  filters: IFilterDropdownProps['filters'];
}

export interface IFilterDropdownProps {
  filters: {
    status: IOptionType[];
    country: IOptionType[];
    suburb: IOptionType[];
    rating: number[];
    excludeApplicantsWithoutRating: boolean;
    activeApplicationsOnly: boolean;
  };
  setFilters: Dispatch<SetStateAction<IFilterDropdownProps['filters']>>;
  getApplications: IGetApplications;
  statusList: IApplicationStatus[];
  countryList: IApplicationCountry[];
  suburbList: IApplicationCountry[];
  setUserPreferences: TSetUserPreferences;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  jobId: number;
  clearAllSelection: () => void;
}

export interface IDropdownProps {
  sortableColumns: typeof sortableColumnsDefaults;
  setSortableColumns: Dispatch<SetStateAction<ISortableColumn[]>>;
  showGender: boolean;
  density: string;
  setDensity: Dispatch<SetStateAction<string>>;
  setUserPreferences: TSetUserPreferences;
}

export type TSetUserPreferences = (
  limit: number | null,
  sort: { sortBy: string; sortOrder: string } | null,
  columns: IDropdownProps['sortableColumns'] | null,
  filters: {
    status: IOptionType[];
    rating: number[];
    excludeApplicantsWithoutRating: boolean;
  } | null,
  density: string | null
) => void;

export interface INewApplicationProps {
  modalsOpen: Record<string, boolean>;
  setModalsOpen: Dispatch<SetStateAction<Record<string, boolean>>>;
  getApplications: IGetApplications;
  jobId: number;
  jobQuestions: IJob['questions'] | undefined;
  jobAttachments: IJob['candidate_attachments'] | undefined;
  jobSources: IJob['sources'] | undefined;
  jobHasPrivacyAcknowledgement: boolean | undefined;
  setSnackbar: StyledSnackbarProps['setSnackbarState'];
  setJob: Dispatch<SetStateAction<IJob | null>>;
}

export interface IActionModalProps {
  modalsOpen: Record<string, boolean>;
  setModalsOpen: Dispatch<SetStateAction<Record<string, boolean>>>;
  globalSelected: IJobApplication[];
  handleDeselectCandidate: (id: number) => void;
  filters: IFilterDropdownProps['filters'] | null;
}

export interface IExternalEmailProps extends IActionModalProps {
  jobId: number;
  setSnackbar: StyledSnackbarProps['setSnackbarState'];
  genderFieldEnabled: boolean;
}

export interface IExtendedActionModalProps extends IActionModalProps {
  jobId: number;
  setSnackbar: StyledSnackbarProps['setSnackbarState'];
}

export interface IAddToEventModalProps extends IExtendedActionModalProps {
  smsTemplatePermissions: boolean;
  calendarDispatch: Dispatch<ScoutCalendarAction>;
  ScoutCalendarState: ScoutCalendarState;
}

export interface ISMSModalProps extends IExtendedActionModalProps {
  hasTemplatePermissions: boolean;
}
export interface ISelectedApplicantsProps {
  globalSelected: IJobApplication[];
  handleDeselectCandidate: (id: number) => void;
  closeModalCallback: () => void;
  filters: IFilterDropdownProps['filters'] | null;
  eventCandidates?: number[];
  manageTagCandidates?: number[];
  modifiedTags?: string[];
  removedTags?: string[];
}

export type IGetApplications = (
  page: number | null,
  limit: number | null,
  searchQuery: string | null,
  sort: { sortBy: string; sortOrder: string } | null,
  filters: IFilterDropdownProps['filters'] | null
) => void;

export type IGetAllApplications = (
  limit: number,
  searchQuery: string | null,
  sort: { sortBy: string; sortOrder: string } | null,
  filters: IFilterDropdownProps['filters'] | null
) => Promise<IJobApplication[]>;

export interface IEmailTemplate {
  id: number;
  name: string;
  subject: string;
  body: string;
}

export interface ISMSTemplate {
  id: number;
  title: string;
  body: string;
}

export interface IEvent {
  id: number;
  subject: string;
  note: string;
  begins_at: string;
  ends_at: string;
  booked: boolean;
  date: string;
  max_candidates: number;
  attendees: IAttendee[];
  job_title: string;
  location: {
    type: string;
    detail: string;
  };
  event_batch_id: number;
  duration: string;
  created_at: string;
  duration_in_minutes: number;
  event_type: IEventType;
  owner: string;
  owner_id: number;
  available_spots: number;
  jobs: {
    id: number;
    title: string;
    reference: string;
  };
}

export enum NoteState {
  PERMANENT_RECORD = 'permanent_record',
  NOTEPAD = 'note_pad'
}

export interface INoteProps {
  anchorEl: HTMLButtonElement | null;
  applicationId: number;
  handleCloseNote: () => void;
  jobId: number;
  onSuccess: () => void;
  setApplications: Dispatch<SetStateAction<IJobApplication[]>>;
  setSnackbarState: StyledSnackbarProps['setSnackbarState'];
  noteState: NoteState | null;
}

export enum SortableColumnId {
  CANDIDATE = 'candidate',
  CANDIDATE_TAGS = 'candidate_tags',
  COUNTRY = 'country',
  DATE = 'date',
  GENDER = 'gender',
  ID = 'id',
  NOTE = 'note', //TODO: to be updated after finalising 'notes' implementation
  NOTES = 'notes',
  PERMANENT_RECORD = 'permanent_record',
  NOTE_PAD = 'note_pad',
  PHONE = 'phone',
  RATING = 'rating',
  STATE = 'state',
  STATUS = 'status',
  SUBURB = 'suburb',
  EMAIL = 'email'
}

export interface IUIPreferences {
  id: string;
  row_count: number;
  sortable_columns: ISortableColumns;
}

export interface ISortableColumn {
  id: SortableColumnId;
  label: string;
  sortable: boolean;
  canToggle: boolean;
  enabled: boolean;
}

export interface IEditableNotes {
  application: IJobApplication;
  setApplications: Dispatch<SetStateAction<IJobApplication[]>>;
  jobId: number;
  setSnackbarState: StyledSnackbarProps['setSnackbarState'];
  noteRef: (el: HTMLInputElement) => void;
}

export interface IRecruiter {
  id: number;
  name: string;
}

export interface InviteToTimeslotStep1Props {
  eventTypes: IEventType[];
  selectedEventType: IEventType | null;
  setSelectedEventType: (type: IEventType | null) => void;
  errors: Record<string, string>;
  setErrors: (errors: Record<string, string>) => void;
  filterType: 'owner' | 'job' | 'recruiter';
  setFilterType: (type: 'owner' | 'job' | 'recruiter') => void;
  selectedRecruiter: IRecruiter | null;
  setSelectedRecruiter: (recruiter: IRecruiter | null) => void;
  selectedJob: string | null;
  setSelectedJob: (job: string | null) => void;
  recruiters: IRecruiter[];
  loadingRecruiters: boolean;
  jobOptions: string[] | undefined;
  loadingJobs: boolean;
  events: IEvent[];
  allEvents: IEvent[];
  isLoading: {
    events: boolean;
    eventTypes: boolean;
  };
  spots: number;
  globalSelected?: IJobApplication[];
  handleDeselectCandidate?: (id: number) => void;
  filters?: IFilterDropdownProps['filters'] | null;
  selectedDay: Date | null;
  setSelectedDay: (date: Date | null) => void;
  selectedMonth: string;
  setSelectedMonth: (date: string) => void;
  timeslotHovered: number | null;
  setTimeslotHovered: (id: number | null) => void;
  eventTypeRef: React.RefObject<HTMLDivElement>;
  isApplicationPage: boolean;
}
